import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, Divider, Grid2 as Grid, Stack, Typography, AppBar, useScrollTrigger } from "@mui/material";
import Scrollbars from "react-custom-scrollbars-2";
import { fetchCategories } from "../categories/slice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { Category, Content, ContentStatus } from "@/types";
import { openItem } from "@/store/uiSlice";
import DishCard from "./components/DishCard";
import DrinksCard from "./components/DrinksCard";
import ProductDetails from "../product-details/ProductDetails";
import useLoadingScreen from "@/hooks/useLoadingScreen";
import { addItemToQuote } from "@/store/quoteItemSlice";
import { IMAGE_PLACEHOLDER } from "@/constants/global";

const ElevationScroll = ({ children, window }: { children: any; window?: any }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    target: window ? window() : undefined,
    threshold: 250,
  });

  return children
    ? React.cloneElement(children, {
        elevation: trigger ? 4 : 0,
        color: trigger ? "white" : "transparent",
      })
    : null;
};

const ActiveSection = ({ children, onActive }: { children: React.ReactNode; id: string; onActive: () => void }) => {
  const sectionRef = useRef<HTMLInputElement>(null);

  const onScroll = () => {
    if (sectionRef.current) {
      const section = sectionRef.current.getBoundingClientRect();

      if (section.bottom - 60 < section.height && section.bottom > 0) {
        onActive();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
  }, []);

  return <div ref={sectionRef}>{children}</div>;
};

const Catalog = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const org = useAppSelector((state) => state.organizations[organizationId]);
  const categories = useAppSelector((state) => state.categories);
  const content = useAppSelector((state) => state.organizations[organizationId]?.content);

  const [activeCategory, seActiveCategory] = useState("");
  const [isLoading, screen] = useLoadingScreen();

  const onAdd = (item: Content) => {
    dispatch(addItemToQuote(item));
  };

  const publishedContent = content?.filter((c) => c.status === ContentStatus.PUBLISHED);

  const scroll = (target: string) => {
    const element = document.getElementById(`category-${target}`);

    if (element) {
      window.scrollTo({
        top: element.offsetTop - 50,
        behavior: "smooth",
      });
    }
  };

  const isFoodIndustry = Boolean(org?.industry === "food-and-beverages");
  const showPrices = org?.industry !== "commercial-printing";

  const productsGrouped = useMemo(() => {
    if (!publishedContent ?? !categories) return {};

    const draft = publishedContent?.reduce((acc, curr) => {
      const publishedContentList = acc[curr.category]?.products ?? [];
      const category = categories[curr.category] ?? {};

      return {
        ...acc,
        [curr.category]: {
          ...category,
          products: [...publishedContentList, curr],
        },
      };
    }, {} as Record<string, Category & { products: Content[] }>);

    return draft;
  }, [categories, publishedContent]);

  const selectedCategory = useMemo(() => {
    const qs = new URLSearchParams(document.location.search);

    const category: any = qs.get("category");

    if (productsGrouped && category && productsGrouped[category]) {
      return category;
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsGrouped, document.location.search]);

  const addCategoryToUrl = (category: string) => {
    const qs = new URLSearchParams(document.location.search);
    qs.set("category", category);
    const path = document.location.pathname;
    navigate(path + "?" + qs.toString());
  };

  const addProductUrl = (productId: string) => {
    const qs = new URLSearchParams(document.location.search);
    qs.set("product", productId);
    const path = document.location.pathname;
    navigate(path + "?" + qs.toString());
  };

  const removeProductUrl = () => {
    const qs = new URLSearchParams(document.location.search);
    qs.delete("product");
    const url = document.location.pathname + "?" + qs.toString();
    navigate(url, { replace: false });
  };

  const selectedProduct =
    (useMemo(() => {
      const qs = new URLSearchParams(document.location.search);
      const productId = qs.get("product");

      if (publishedContent && productId) {
        return publishedContent.find((c) => c.id === productId);
      }
      return undefined;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publishedContent, document.location.search]) as Content) ?? undefined;

  useEffect(() => {
    if (selectedProduct) {
      dispatch(openItem(selectedProduct));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (selectedCategory) {
      seActiveCategory(selectedCategory);
      scroll(selectedCategory);
    }
  }, [selectedCategory]);

  if (isLoading) {
    return screen;
  }

  return (
    <>
      <ElevationScroll>
        <AppBar position="sticky">
          <Scrollbars autoHide style={{ height: "65px" }}>
            <Stack className="category-chips" direction={"row"} spacing={1} py={2} px={3}>
              {Object.values(productsGrouped ?? {}).map((c) => (
                <Chip
                  key={c.id}
                  label={`${c.title} ${c.products.length}`}
                  onClick={() => addCategoryToUrl(c.id)}
                  color="primary"
                  variant={activeCategory === c.id ? "filled" : "outlined"}
                />
              ))}
            </Stack>
          </Scrollbars>
        </AppBar>
      </ElevationScroll>

      <Stack spacing={2}>
        {Object.values(productsGrouped ?? {}).map((cat) => {
          if (!cat.products?.length) return null;
          return (
            <ActiveSection key={cat.id} id={`category-${cat.id}`} onActive={() => seActiveCategory(cat.id)}>
              <Stack id={`category-${cat.id}`}>
                <Stack padding={3} spacing={2}>
                  <Stack direction={"row"} spacing={2} alignItems={"center"}>
                    <Typography variant="h3" color="primary">
                      {` ${cat.title}`}
                    </Typography>
                    <Chip label={cat.products.length} color="default" size="small" variant="outlined" />
                  </Stack>
                  <Grid container spacing={cat.id === "drinks" ? 2 : 4}>
                    {cat.products?.map((cont) => (
                      <Grid key={cont.id} size={{ md: 6, xs: 9 }} sx={{ width: "100%" }}>
                        {isFoodIndustry && cat.id === "drinks" ? (
                          <DrinksCard
                            title={cont.title}
                            image={cont.images?.[0] ?? IMAGE_PLACEHOLDER}
                            description={cont.description}
                            price={showPrices ? (("$" + cont.price) as unknown as string) : null}
                            onClick={() => addProductUrl(cont.id)}
                          />
                        ) : (
                          <DishCard
                            title={cont.title}
                            image={cont.images?.[0] ?? IMAGE_PLACEHOLDER}
                            previewDescription={cont.previewDescription}
                            description={cont.description}
                            price={showPrices ? (("$" + cont.price) as unknown as string) : null}
                            onClick={() => addProductUrl(cont.id)}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
                <Divider />
              </Stack>
            </ActiveSection>
          );
        })}
      </Stack>
      <ProductDetails enableOrders={false} onClose={removeProductUrl} addItem={onAdd} />
    </>
  );
};

export default Catalog;
